import {
  ButtonHTMLAttributes,
  FC,
  MouseEvent,
  ReactNode,
  useState,
} from "react";
import { Button } from "../Button/Button";
import { Icon, type IconName } from "../Icons";
import "./ActionButton.css";
import { delay } from "./utils";

/**
 * ActionButton component props
 * @interface ActionButtonProps
 * @extends ButtonHTMLAttributes<HTMLButtonElement>
 */
/**
 * Interface defining confirmation options for ActionButton
 */
export interface ConfirmationOptions {
  /**
   * Title for confirmation dialog
   */
  title: string;

  /**
   * Subtitle/description for confirmation dialog
   */
  subtitle?: string;

  /**
   * Function to be called when user confirms the action
   */
  onConfirm: () => void;
}

export interface ActionButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Click handler for the button
   * Can return a Promise for async operations
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>;

  /**
   * Text to display on the button
   */
  text: string;

  /**
   * Visual style of the button
   * @default "primary"
   */
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "warning"
    | "success"
    | "ghost"
    | "link";

  /**
   * Size of the button
   * @default "md"
   */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /**
   * Name of icon to show on the button
   */
  iconName?: IconName;

  /**
   * Icon component to show on the button, alternative to iconName
   */
  svgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;

  /**
   * Position of the icon relative to text
   * @default "left"
   */
  iconPosition?: "left" | "right";

  /**
   * Whether to display loading state when action is processing
   * @default true
   */
  showLoading?: boolean;

  /**
   * Externally-controlled loading state, OR-ed with the internal
   * (await-driven) state. Use when the work that should keep the button in a
   * loading state runs outside this button's own `onClick` promise — e.g. an
   * action kicked off asynchronously via a modal confirm.
   */
  loading?: boolean;

  /**
   * Handler for errors during async actions
   */
  onError?: (error: unknown) => void;

  /**
   * Whether to show success indicator after successful action
   * @default false
   */
  showSuccess?: boolean;

  /**
   * Text to display during success state (e.g. "Approved" instead of "Approve")
   */
  successText?: string;

  /**
   * Duration to show success indicator in milliseconds
   * @default 2000
   */
  successDuration?: number;

  /**
   * Whether to show confirmation before executing action
   * @default false
   */
  requireConfirmation?: boolean;

  /**
   * Title for confirmation dialog
   */
  confirmationTitle?: string;

  /**
   * Subtitle/description for confirmation dialog
   */
  confirmationSubtitle?: string;

  /**
   * Callback to execute after successful action completion
   */
  onSuccess?: () => void;

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Children nodes
   */
  children?: ReactNode;

  /** Collapse label until hover/focus */
  collapseToIcon?: boolean;

  /**
   * Custom confirmation handler for handling confirmation dialogs
   * If provided, this will be used instead of window.confirm
   * This allows for integration with any UI confirmation system
   *
   * @example
   * // Using with custom confirmation system
   * <ActionButton
   *   text="Delete"
   *   requireConfirmation
   *   confirmationTitle="Confirm deletion"
   *   confirmationHandler={({ title, subtitle, onConfirm }) => {
   *     showCustomConfirmDialog({
   *       title,
   *       message: subtitle,
   *       onOk: onConfirm
   *     });
   *   }}
   * />
   */
  confirmationHandler?: (options: ConfirmationOptions) => void;
}

/**
 * ActionButton component
 *
 * An enhanced button that handles loading states, success indicators, and confirmation
 * dialogs for actions. It extends the base Button component with async action support.
 *
 * The component supports dependency injection for confirmation dialogs, allowing integration
 * with any UI confirmation system by passing a custom confirmationHandler.
 *
 * @example
 * // Basic usage
 * <ActionButton text="Save" onClick={handleSave} />
 *
 * // With success indicator and loading state
 * <ActionButton text="Submit" onClick={submitData} showSuccess />
 *
 * // With built-in confirmation dialog (uses window.confirm)
 * <ActionButton
 *   text="Delete"
 *   variant="danger"
 *   onClick={handleDelete}
 *   requireConfirmation
 *   confirmationTitle="Confirm Deletion"
 *   confirmationSubtitle="This action cannot be undone."
 * />
 *
 * // With custom confirmation dialog system
 * <ActionButton
 *   text="Delete"
 *   variant="danger"
 *   onClick={handleDelete}
 *   requireConfirmation
 *   confirmationTitle="Confirm Deletion"
 *   confirmationSubtitle="This action cannot be undone."
 *   confirmationHandler={({ title, subtitle, onConfirm }) => {
 *     // Use your own confirmation dialog system
 *     useConfirmation().showConfirmation({
 *       title,
 *       subtitle,
 *       onConfirm
 *     });
 *   }}
 * />
 */
export const ActionButton: FC<ActionButtonProps> = ({
  onClick,
  text,
  variant = "primary",
  size = "md",
  iconName,
  svgIcon: SvgIcon,
  iconPosition = "left",
  showLoading = true,
  loading = false,
  disabled = false,
  onError,
  showSuccess = false,
  successText,
  successDuration = 2000,
  onSuccess,
  requireConfirmation = false,
  confirmationTitle,
  confirmationSubtitle,
  confirmationHandler,
  className = "",
  children,
  collapseToIcon = false,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // OR the internal await-driven state with the externally-controlled prop so
  // callers can keep the button loading while async work runs outside onClick.
  const effectiveLoading = isLoading || loading;

  // Determine icon to show based on state
  const iconElement = isSuccess ? (
    <Icon name="checkbox-checked" className="indigo-action-button-icon" />
  ) : iconName && !effectiveLoading ? (
    <Icon name={iconName} className="indigo-action-button-icon" />
  ) : SvgIcon && !effectiveLoading ? (
    <SvgIcon className="indigo-action-button-icon" width={20} height={20} />
  ) : null;

  /**
   * Handles the button action with loading state and error handling
   * @param event - The click event
   */
  const handleAction = async (event: MouseEvent<HTMLButtonElement>) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      await onClick?.(event);
      if (showSuccess) {
        setIsSuccess(true);
        await delay(successDuration);
        setIsSuccess(false);
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        console.error("Action failed:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles click with optional confirmation dialog
   * @param event - The click event
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (requireConfirmation && confirmationTitle) {
      // Create a handler that will process the action when confirmed
      const onConfirm = () => {
        void handleAction(event);
      };
      // If custom confirmation handler is provided, use it
      if (confirmationHandler) {
        confirmationHandler({
          title: confirmationTitle,
          subtitle: confirmationSubtitle,
          onConfirm,
        });
      } else {
        // Otherwise fall back to built-in browser confirm dialog
        if (
          window.confirm(`${confirmationTitle}\n${confirmationSubtitle || ""}`)
        ) {
          onConfirm();
        }
      }
    } else {
      void handleAction(event);
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      size={size}
      disabled={disabled || effectiveLoading}
      loading={effectiveLoading && showLoading}
      icon={iconElement}
      iconPosition={iconPosition}
      className={`indigo-action-button ${className}`}
      collapseToIcon={collapseToIcon}
      {...props}
    >
      {children || (isSuccess && successText ? successText : text)}
    </Button>
  );
};

export default ActionButton;
